import type {
  TaskExecutionSummary,
  TaskView,
  WorkflowView,
} from '@/lib/api/generated/model';
import {
  boardCompletion,
  buildBoard,
  hasNonTerminalActivity,
  stepLabelsOf,
  type Board,
  type BoardStepSection,
  type TaskLocation,
} from '@/features/tasks';

import type {
  RunBreakdown,
  RunNextStep,
  RunResolution,
  RunSummary,
} from './types';

/**
 * Buckets the run's tasks for the segmented bar (`RunBreakdown` documents the
 * precedence). "Done" is read off the board's own `systemRole: 'done'` column,
 * the same reading `boardCompletion` takes, so the bar's done segment and the
 * "n/m done" words above it can never disagree.
 */
function runBreakdown(board: Board, tasks: TaskView[]): RunBreakdown {
  const doneIds = new Set(
    board.columns
      .filter((column) => column.systemRole === 'done')
      .flatMap((column) => column.steps.flatMap((step) => step.tasks))
      .map((task) => task.id),
  );
  const breakdown: RunBreakdown = {
    done: 0,
    needsAttention: 0,
    needsReview: 0,
    running: 0,
    idle: 0,
  };
  for (const task of tasks) {
    if (doneIds.has(task.id)) breakdown.done += 1;
    else if (task.needs_attention) breakdown.needsAttention += 1;
    else if (task.needs_review) breakdown.needsReview += 1;
    else if (hasNonTerminalActivity([task])) breakdown.running += 1;
    else breakdown.idle += 1;
  }
  return breakdown;
}

/**
 * The step the parent moves to when the join closes. A `linked_workflow` step
 * has exactly one transition (backend spec 097 FR-005); its name comes from
 * the parent board's own labels, which include the terminal `$done` step.
 */
function nextStepOf(
  step: BoardStepSection,
  parentStepLabels: Map<string, string>,
): RunNextStep | null {
  const target = step.transitions?.[0]?.to;
  if (!target) return null;
  const name = parentStepLabels.get(target);
  return name === undefined ? null : { id: target, name };
}

/** The newest of the parent's `linked_workflow` runs, `created_at` then id. */
function latestLinkedRun(
  executions: TaskExecutionSummary[],
): TaskExecutionSummary | undefined {
  return executions
    .filter((run) => run.step_kind_snapshot === 'linked_workflow')
    .sort((a, b) =>
      a.created_at !== b.created_at
        ? a.created_at < b.created_at
          ? 1
          : -1
        : a.id < b.id
          ? 1
          : -1,
    )[0];
}

/**
 * Counts across a run's tasks that no board-shaped structure carries —
 * `needs_review`/`needs_attention` are per-task flags, not a column
 * membership like "done" is (contrast `boardCompletion`, which reads
 * `systemRole: 'done'` off the board rather than a `TaskView` field).
 */
function reviewAndAttentionCounts(tasks: TaskView[]): {
  needsReview: number;
  needsAttention: number;
} {
  let needsReview = 0;
  let needsAttention = 0;
  for (const task of tasks) {
    if (task.needs_review) needsReview += 1;
    if (task.needs_attention) needsAttention += 1;
  }
  return { needsReview, needsAttention };
}

export interface ResolveRunInput {
  parentTask: TaskView;
  allTasks: TaskView[];
  /** The parent's own workflow board — where the run's step is looked up. */
  parentBoard: Board | undefined;
  /**
   * Where that board placed the parent now — the same object
   * `TaskDetailsPanel`/`TaskCard` compute via `locateTask`, so the page can
   * never disagree with the drawer about which step the parent is on.
   */
  location: TaskLocation | undefined;
  workflows: WorkflowView[];
  /**
   * A specific run to show, from the address (`?execution=`) — how the parent's
   * Executions tab opens a past visit. Absent means the latest run.
   */
  executionId?: string;
}

/**
 * Resolves one of a parent task's `linked_workflow` runs, open or finished, or
 * reports that there is none to show.
 *
 * **Which run.** The one `executionId` names, when it is one of the parent's
 * `linked_workflow` runs; otherwise the newest such run. There is no "current
 * execution id" field on `TaskView`, so both come from `parentTask.executions`.
 * An `executionId` naming anything else — another task's run, an AI step's, a
 * typo — is `not-linked` rather than a quiet fall-back to the latest, which
 * would show a different run under the address the reader followed.
 *
 * **Open or closed.** Open while that run sits in `awaiting_children` *and*
 * the parent still stands on its step — both, because either alone can lag the
 * other by one poll. Anything else is a finished run, shown read-only.
 *
 * **Which step.** The run's own `step_id_snapshot`, looked up on the parent's
 * board as it stands now: that is where the target workflow and the step's
 * prompt and transition come from. A step since removed from the workflow
 * leaves nothing to resolve the target from, so that run is `not-linked`.
 *
 * The returned `Board` is built with `buildBoard` over the run's task set only
 * — the function `BoardPage` uses for the target workflow's own board — so
 * `done`/`total` come from `boardCompletion` (the board's own `systemRole:
 * 'done'` column), not a second, run-page-only notion of "done" (spec 115
 * Edge Cases).
 */
export function resolveRun({
  parentTask,
  allTasks,
  parentBoard,
  location,
  workflows,
  executionId,
}: ResolveRunInput): RunResolution {
  const executions = parentTask.executions ?? [];
  const execution =
    executionId === undefined
      ? latestLinkedRun(executions)
      : executions.find(
          (run) =>
            run.id === executionId &&
            run.step_kind_snapshot === 'linked_workflow',
        );
  if (execution === undefined || parentBoard === undefined) {
    return { status: 'not-linked' };
  }

  const step = parentBoard.columns
    .flatMap((column) => column.steps)
    .find((candidate) => candidate.stepId === execution.step_id_snapshot);
  if (step === undefined || step.kind !== 'linked_workflow') {
    return { status: 'not-linked' };
  }

  const targetWorkflow = workflows.find(
    (candidate) => candidate.key === step.workflow,
  );
  if (targetWorkflow === undefined) {
    return { status: 'not-linked' };
  }

  // Membership is the parent link, not the creating execution: a rerun or a
  // split (spec 119) moves subtasks between parents without touching
  // `created_by_execution_id`, which is provenance only. The newest run shows
  // the current subtasks of its own step's batch — reruns of that step share
  // one batch, but a different sub-workflow step starts its own (spec 132) —
  // while an older run shows only the ones it created that are still under
  // this parent, so a split-off task does not reappear there. A subtask whose
  // creating run is unknown here counts toward the batch, as on the server.
  const isLatest = execution.id === latestLinkedRun(executions)?.id;
  const stepOfRun = new Map(
    executions.map((run) => [run.id, run.step_id_snapshot]),
  );
  const tasks = allTasks.filter((task) => {
    if (task.parent_task_id !== parentTask.id) return false;
    if (!isLatest) return task.created_by_execution_id === execution.id;
    const creatingStep = task.created_by_execution_id
      ? stepOfRun.get(task.created_by_execution_id)
      : undefined;
    return (
      creatingStep === undefined || creatingStep === execution.step_id_snapshot
    );
  });

  const built = buildBoard(targetWorkflow, tasks);
  // A target workflow with no workable step is exactly as unusable for the
  // run page as it would be for the real board — fall back to "not-linked"
  // rather than fabricate a board `BoardCanvas` was never meant to render.
  if (!built.ok) {
    return { status: 'not-linked' };
  }
  const board: Board = built.board;
  const { done, total } = boardCompletion(board);
  const { needsReview, needsAttention } = reviewAndAttentionCounts(tasks);
  const open =
    execution.status === 'awaiting_children' &&
    location?.step.stepId === execution.step_id_snapshot;

  const summary: RunSummary = {
    executionId: execution.id,
    parentTask: {
      id: parentTask.id,
      title: parentTask.title,
      workflow_id: parentTask.workflow_id,
    },
    targetWorkflowId: targetWorkflow.id,
    targetWorkflowName: targetWorkflow.name,
    targetWorkflowKey: targetWorkflow.key,
    stepName: step.label,
    done,
    total,
    needsReview,
    needsAttention,
    breakdown: runBreakdown(board, tasks),
    stepPrompt: step.prompt?.trim() ? step.prompt : null,
    nextStep: nextStepOf(step, stepLabelsOf(parentBoard)),
    execution,
    closed: !open,
    parentStepNow: location?.stepLabel ?? null,
    scheduler: parentTask.linked_tasks?.scheduler ?? null,
  };

  return { status: 'ready', summary, tasks, board };
}
